import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import xss from "xss";
import classNames from "classnames";
import { Copy } from "lucide-react";
import { backendLink } from "../config";
import MetaTags from "../components/MetaTagsIndex";
import {
  styles,
  ErrorBox,
  Field,
  LocationFields,
  PageShell,
  Select,
  Sheet,
  localized,
  spacedPhone,
  toDigits,
  useLocations,
  cleanText,
  flagField,
} from "../components/wizard";

// Letters, digits, Arabic and accented letters: what the previous form kept in free-text fields
const cleanField = (value) => cleanText(xss(value)).slice(0, 25);

// Merchant sign-up (/inscription_d): done once, the merchant then receives their code
export default function InscriptionMerchant({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const merchantSubscriptionFields = useSelector((state) => state.tenant.tenant.merchantSubscriptionFields) || [];
  const obligatoryMerchantSubscriptionFields = useSelector((state) => state.tenant.tenant.obligatoryMerchantSubscriptionFields) || [];
  const has = (field) => merchantSubscriptionFields.includes(field);
  const isRequired = (field) => obligatoryMerchantSubscriptionFields.includes(field);

  // synchronize language switch
  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  const locations = useLocations();
  const [merchantTypes, setMerchantTypes] = useState([]);
  useEffect(() => {
    axios
      .get(`${backendLink}merchant/getMerchantTypes`)
      .then((response) => setMerchantTypes(response.data?.data?.merchant_types || []))
      .catch((error) => console.error("Error fetching merchant types:", error));
  }, []);

  const [marketName, setMarketName] = useState("");
  const [phone, setPhone] = useState("");
  const [place, setPlace] = useState({ wilayaId: "", commune: "" });
  const [typeId, setTypeId] = useState("");
  const [idh, setIdh] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [merchantCode, setMerchantCode] = useState(""); // set once the account is created
  const [copied, setCopied] = useState(false);

  const wilaya = locations.wilayas.find((w) => w.id === place.wilayaId);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    const filled = {
      market_name: marketName.trim(),
      idh: idh.trim(),
      phone,
      willaya: wilaya,
      commune: place.commune,
      merchant_type_id: typeId,
    };
    const missing = obligatoryMerchantSubscriptionFields.find((field) => !filled[field]);
    if (missing) {
      flagField(missing);
      return setError(t([`rmReq_${missing}`, missing]));
    }

    const formData = new FormData();
    marketName && formData.append("market_name", marketName);
    wilaya && formData.append("willaya", wilaya.text);
    place.commune && formData.append("commune", place.commune);
    phone && formData.append("phone", spacedPhone(phone));
    typeId && formData.append("merchant_type_id", typeId);
    idh && formData.append("idh", idh);

    setError("");
    setSending(true);
    try {
      const response = await axios.post(`${backendLink}merchant/registerMerchant`, formData, {
        withCredentials: true, // Include cookies for CSRF protection
      });
      if (response.status === 201) setMerchantCode(response.data.data.merchant_code);
    } catch (err) {
      console.error("Request failed:", err.response || err);
      setError(err.response?.status === 422 ? t("phoneUsed") : t("suggestionErrorServer"));
    } finally {
      setSending(false);
    }
  };

  const copyCode = () => {
    try {
      navigator.clipboard.writeText(merchantCode);
    } catch (_) {}
    setCopied(true);
  };

  const goToComplaint = () => navigate("/reclamation_d");

  return (
    <PageShell title={t("rcComplaint")} onBack={() => navigate("/reclamation_d")} currentLang={currentLang} setCurrentLang={setCurrentLang}>
      <MetaTags />
      <div className={styles.simpleBody}>
        <p className={styles.pageQ} style={{ marginBottom: 4 }}>
          {t("rmRegTitle")}
        </p>
        <p className={styles.ctxLine}>{t("rmRegSub")}</p>
        <form noValidate onSubmit={onSubmit}>
          {has("market_name") && (
            <Field label={t("rmRegStore")} required={isRequired("market_name")} field="market_name">
              <input
                type="text"
                className={styles.input}
                autoComplete="organization"
                maxLength="25"
                value={marketName}
                onChange={(e) => setMarketName(cleanField(e.target.value))}
              />
            </Field>
          )}
          {has("phone") && (
            <Field label={t("rcPhone")} required={isRequired("phone")} field="phone">
              <input
                type="tel"
                className={classNames(styles.input, styles.ltr)}
                autoComplete="tel"
                inputMode="numeric"
                placeholder="0555123456"
                value={phone}
                onChange={(e) => setPhone(toDigits(e.target.value).slice(0, 10))}
              />
            </Field>
          )}
          {has("willaya") && (
            <LocationFields
              locations={locations}
              value={place}
              onChange={setPlace}
              isRequired={{ wilaya: isRequired("willaya"), commune: isRequired("commune") }}
            />
          )}
          {has("merchant_type_id") && (
            <Field label={t("rmRegType")} required={isRequired("merchant_type_id")} field="merchant_type_id">
              <Select
                value={typeId}
                placeholder={t("rmRegTypePh")}
                options={merchantTypes.map((type) => ({ value: String(type.id), label: localized(type, lang) }))}
                onChange={setTypeId}
              />
            </Field>
          )}
          {has("idh") && (
            <Field label={t("IDH")} required={isRequired("idh")} field="idh">
              <input type="text" className={styles.input} autoComplete="off" maxLength="25" value={idh} onChange={(e) => setIdh(cleanField(e.target.value))} />
            </Field>
          )}
          <ErrorBox>{error}</ErrorBox>
          <button type="submit" className={styles.submit} disabled={sending || !!merchantCode}>
            {t("rmRegSubmit")}
          </button>
        </form>
      </div>

      {merchantCode && (
        <Sheet title={<Trans i18nKey="rmCodeOkTitle" components={{ b: <b /> }} />} onClose={goToComplaint}>
          <p className={styles.center} style={{ margin: 0, fontSize: 15, color: "var(--ink-2)" }}>
            {t("rmCodeIs")}
          </p>
          <div className={styles.codeBig}>{merchantCode}</div>
          <button
            type="button"
            className={classNames(styles.ghost, styles.block, { [styles.isOn]: copied })}
            style={{ marginTop: 12 }}
            onClick={copyCode}
          >
            <Copy size={17} aria-hidden />
            <span>{copied ? t("rmCopied") : t("rmCopy")}</span>
          </button>
          <button type="button" className={classNames(styles.next, styles.block)} style={{ marginTop: 10 }} onClick={goToComplaint}>
            {t("rmCodeGo")}
          </button>
        </Sheet>
      )}
    </PageShell>
  );
}
